import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, X, Radio, Newspaper, Eye, EyeOff,
         AlertCircle, CheckCircle, Clock, Star, Zap } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// ErueNewsPortal — Two separate functions:
//   📰 NEWS PORTAL  — full articles, draft/published, categories, featured
//   📡 NEWS BAR     — articles with isBreaking=true feed the scrolling ticker
//                     Admin controls which articles appear in the ticker
// Backend: PostgreSQL newsArticle model
// ─────────────────────────────────────────────────────────────────────────────

const C = {
  bg:'#070D0A', card:'#0E1812', border:'#1C2E22',
  gold:'#C9A84C', goldLt:'#E8C97A', text:'#EDE8DC',
  muted:'rgba(237,232,220,0.5)', red:'#ef4444',
  green:'#22c55e', amber:'#f59e0b', input:'#111A14',
  breaking:'#dc2626',
};

const CATEGORIES = [
  'Festival News','Royal Announcement','Community Update','Sports & Youth',
  'Culture & Heritage','Development','Diaspora','Entertainment','General',
];

const authHdr = () => ({
  Authorization:`Bearer ${localStorage.getItem('erue_auth_token')||''}`,
  'Content-Type':'application/json',
});

const iS: React.CSSProperties = {
  width:'100%', padding:'8px 10px', borderRadius:6,
  background:C.input, border:`1px solid ${C.border}`,
  color:C.text, fontSize:13, outline:'none', boxSizing:'border-box',
};

function Label({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase',
                  letterSpacing:1, color:C.muted, marginBottom:4 }}>
      {children}{required && <span style={{ color:C.red }}> *</span>}
    </div>
  );
}

function Btn({ children, onClick, variant='primary', disabled, style }:
  { children:React.ReactNode; onClick?:()=>void; variant?:string; disabled?:boolean; style?: React.CSSProperties }) {
  const bg = variant==='danger'?C.red:variant==='ghost'?'transparent':variant==='breaking'?C.breaking:C.gold;
  const co = variant==='ghost'?C.muted:'#fff';
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ background:bg, color:variant==='primary'?'#000':co,
               border:variant==='ghost'?`1px solid ${C.border}`:'none',
               borderRadius:6, padding:'7px 14px', fontSize:12, fontWeight:700,
               cursor:disabled?'not-allowed':'pointer', opacity:disabled?0.5:1, ...style }}>
      {children}
    </button>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cfg: Record<string,{color:string;label:string}> = {
    published: { color:C.green,   label:'Published' },
    draft:     { color:C.amber,   label:'Draft' },
    archived:  { color:C.muted,   label:'Archived' },
  };
  const c = cfg[status]||cfg.draft;
  return (
    <span style={{ background:`${c.color}22`, color:c.color, fontSize:10, fontWeight:700,
                    padding:'2px 8px', borderRadius:20, border:`1px solid ${c.color}44` }}>
      {c.label}
    </span>
  );
}

interface Article {
  id:string; title:string; slug:string; subtitle:string|null; excerpt:string|null;
  content:string; category:string; tags:string[]; status:string;
  isFeatured:boolean; isBreaking:boolean; featuredImageUrl:string|null;
  viewCount:number; publishedAt:string|null; createdAt:string; updatedAt:string;
  author?:{ displayName:string|null; email:string }|null;
}

interface Stats { total:number; published:number; drafts:number; breaking:number; }

const emptyForm = () => ({
  title:'', subtitle:'', excerpt:'', content:'', category:'Festival News',
  featuredImageUrl:'', isBreaking:false, isFeatured:false,
  status:'draft', tags:[] as string[],
  targetAudience:[] as string[],
});

interface Props {
  currentUser?: { email?:string; systemRole?:string; role?:string; displayName?:string } | null;
  adminMode?: boolean;
}

export function ErueNewsPortal({ currentUser, adminMode = false }: Props) {
  const [articles, setArticles]     = useState<Article[]>([]);
  const [stats, setStats]           = useState<Stats>({ total:0, published:0, drafts:0, breaking:0 });
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [tab, setTab]               = useState<'portal'|'newsbar'>('portal');
  const [filterCat, setFilterCat]   = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [search, setSearch]         = useState('');
  const [toast, setToast]           = useState<{msg:string;type:'ok'|'err'}|null>(null);
  const [showForm, setShowForm]     = useState(false);
  const [editArticle, setEditArticle] = useState<Article|null>(null);
  const [previewArt, setPreviewArt] = useState<Article|null>(null);
  const [form, setForm]             = useState(emptyForm());
  const [deleteConfirm, setDeleteConfirm] = useState<Article|null>(null);

  const showToast = (msg:string, type:'ok'|'err'='ok') => {
    setToast({msg,type}); setTimeout(()=>setToast(null),3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch('/api/admin/news', { headers: authHdr() });
      const d = await r.json();
      if (d.success) {
        setArticles(d.articles||[]);
        setStats(d.stats||{ total:0, published:0, drafts:0, breaking:0 });
      }
    } catch { showToast('Failed to load articles','err'); }
    setLoading(false);
  }, []);

  useEffect(()=>{ if(adminMode) load(); },[load, adminMode]);

  const upd = (k:string, v:any) => setForm(f=>({...f,[k]:v}));

  const openAdd = () => {
    setEditArticle(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (a: Article) => {
    setEditArticle(a);
    setForm({
      title:a.title, subtitle:a.subtitle||'', excerpt:a.excerpt||'',
      content:a.content||'', category:a.category,
      featuredImageUrl:a.featuredImageUrl||'',
      isBreaking:a.isBreaking, isFeatured:a.isFeatured,
      status:a.status, tags:a.tags||[], targetAudience:[],
    });
    setShowForm(true);
  };

  const saveArticle = async () => {
    if (!form.title) return showToast('Title is required','err');
    if (!form.content && !form.excerpt) return showToast('Content or summary required','err');
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        summary: form.excerpt.trim(),
        fullBody: form.content.trim(),
        category: form.category,
        author: form.subtitle.trim(),
        featuredImageUrl: form.featuredImageUrl.trim()||null,
        isBreaking: form.isBreaking,
        isFeatured: form.isFeatured,
        status: form.status,
        targetAudience: form.tags,
        tags: form.tags,
      };
      const url  = editArticle ? `/api/admin/news/${editArticle.id}` : '/api/admin/news';
      const meth = editArticle ? 'PUT' : 'POST';
      const r    = await fetch(url, { method:meth, headers:authHdr(), body:JSON.stringify(payload) });
      const d    = await r.json();
      if (d.success) {
        showToast(editArticle ? 'Article updated' : 'Article created');
        setShowForm(false); setEditArticle(null);
        load();
      } else showToast(d.error||'Save failed','err');
    } catch(e:any){ showToast(e.message,'err'); }
    setSaving(false);
  };

  const togglePublish = async (a: Article) => {
    try {
      const r = await fetch(`/api/admin/news/${a.id}/publish`, { method:'PUT', headers:authHdr() });
      const d = await r.json();
      if (d.success) { showToast(`Article ${d.status}`); load(); }
      else showToast(d.error||'Failed','err');
    } catch(e:any){ showToast(e.message,'err'); }
  };

  const toggleBreaking = async (a: Article) => {
    try {
      const r = await fetch(`/api/admin/news/${a.id}`, {
        method:'PUT', headers:authHdr(),
        body:JSON.stringify({ isBreaking:!a.isBreaking })
      });
      const d = await r.json();
      if (d.success) {
        showToast(!a.isBreaking ? '📡 Added to News Bar ticker' : '📡 Removed from News Bar ticker');
        load();
      } else showToast(d.error||'Failed','err');
    } catch(e:any){ showToast(e.message,'err'); }
  };

  const deleteArticle = async (a: Article) => {
    try {
      const r = await fetch(`/api/admin/news/${a.id}`, { method:'DELETE', headers:authHdr() });
      const d = await r.json();
      if (d.success) { showToast('Article deleted'); setDeleteConfirm(null); load(); }
      else showToast(d.error||'Failed','err');
    } catch(e:any){ showToast(e.message,'err'); }
  };

  // Filter logic
  const filtered = articles.filter(a=>{
    const matchCat    = filterCat==='All' || a.category===filterCat;
    const matchStatus = filterStatus==='All' || a.status===filterStatus;
    const matchSearch = !search || a.title.toLowerCase().includes(search.toLowerCase())
                        || (a.excerpt||'').toLowerCase().includes(search.toLowerCase());
    return matchCat && matchStatus && matchSearch;
  });

  const breakingArticles = articles.filter(a=>a.isBreaking && a.status==='published');
  const portalArticles   = filtered;

  const fmtDate = (s:string|null) => s ? new Date(s).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '—';

  if (!adminMode) {
    // Public read-only view
    return (
      <div style={{ background:C.bg, minHeight:'100vh', padding:'20px 16px', fontFamily:'Plus Jakarta Sans, sans-serif' }}>
        <h2 style={{ fontFamily:'Cinzel, serif', color:C.gold, marginBottom:20 }}>News & Announcements</h2>
        <div style={{ color:C.muted, fontSize:13 }}>Loading news portal...</div>
      </div>
    );
  }

  return (
    <div style={{ background:C.bg, minHeight:'100vh', padding:'20px 16px', fontFamily:'Plus Jakarta Sans, sans-serif' }}>

      {/* Toast */}
      {toast && (
        <div style={{ position:'fixed', top:20, right:20, zIndex:9999,
                       background:toast.type==='ok'?C.green:C.red, color:'#fff',
                       borderRadius:8, padding:'10px 18px', fontSize:13, fontWeight:700,
                       boxShadow:'0 4px 20px rgba(0,0,0,0.4)' }}>
          {toast.type==='ok'?'✅':'❌'} {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h2 style={{ fontFamily:'Cinzel, serif', color:C.gold, margin:0, fontSize:20 }}>News Management</h2>
          <p style={{ color:C.muted, fontSize:12, margin:'4px 0 0' }}>
            Manage articles for the News Portal · Toggle "Breaking" to add to the scrolling News Bar ticker
          </p>
        </div>
        <Btn onClick={openAdd}><Plus size={14} style={{ marginRight:4 }}/>New Article</Btn>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(110px,1fr))', gap:10, marginBottom:20 }}>
        {[
          { label:'Total', value:stats.total, color:C.gold },
          { label:'Published', value:stats.published, color:C.green },
          { label:'Drafts', value:stats.drafts, color:C.amber },
          { label:'📡 In News Bar', value:stats.breaking, color:C.breaking },
        ].map(s=>(
          <div key={s.label} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:8, padding:'12px 14px' }}>
            <div style={{ fontSize:20, fontWeight:800, color:s.color }}>{s.value}</div>
            <div style={{ fontSize:10, color:C.muted, textTransform:'uppercase', letterSpacing:1 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:8, marginBottom:20 }}>
        <button onClick={()=>setTab('portal')}
          style={{ padding:'7px 18px', borderRadius:6, fontSize:12, fontWeight:700, cursor:'pointer',
                    background:tab==='portal'?C.gold:'transparent', color:tab==='portal'?'#000':C.muted,
                    border:`1px solid ${tab==='portal'?C.gold:C.border}` }}>
          <Newspaper size={13} style={{ marginRight:5, verticalAlign:'middle' }}/>
          News Portal Articles
        </button>
        <button onClick={()=>setTab('newsbar')}
          style={{ padding:'7px 18px', borderRadius:6, fontSize:12, fontWeight:700, cursor:'pointer',
                    background:tab==='newsbar'?C.breaking:'transparent',
                    color:tab==='newsbar'?'#fff':C.muted,
                    border:`1px solid ${tab==='newsbar'?C.breaking:C.border}` }}>
          <Radio size={13} style={{ marginRight:5, verticalAlign:'middle' }}/>
          News Bar Ticker ({stats.breaking})
        </button>
      </div>

      {/* NEWS BAR TAB */}
      {tab==='newsbar' && (
        <div>
          <div style={{ background:`${C.breaking}11`, border:`1px solid ${C.breaking}33`,
                         borderRadius:10, padding:'14px 16px', marginBottom:16 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
              <Radio size={16} color={C.breaking}/>
              <span style={{ color:C.breaking, fontWeight:800, fontSize:14 }}>News Bar Ticker</span>
              <span style={{ background:C.breaking, color:'#fff', fontSize:10, fontWeight:800,
                              padding:'2px 8px', borderRadius:20 }}>LIVE SCROLLING</span>
            </div>
            <p style={{ color:C.muted, fontSize:12, margin:0 }}>
              Articles marked as "Breaking" appear in the scrolling ticker bar displayed across all public pages.
              Only <strong style={{ color:C.text }}>Published</strong> + <strong style={{ color:C.text }}>Breaking</strong> articles appear in the ticker.
              Toggle Breaking status on any published article below to add/remove it from the ticker.
            </p>
          </div>

          {/* Live ticker preview */}
          {breakingArticles.length>0 && (
            <div style={{ background:C.breaking, borderRadius:8, padding:'10px 16px', marginBottom:16,
                           display:'flex', alignItems:'center', gap:12, overflow:'hidden' }}>
              <span style={{ color:'#fff', fontSize:11, fontWeight:800, flexShrink:0, letterSpacing:1 }}>
                LIVE TICKER:
              </span>
              <div style={{ flex:1, overflow:'hidden', whiteSpace:'nowrap' }}>
                <span style={{ color:'#fff', fontSize:12 }}>
                  {breakingArticles.map(a=>a.title).join('  ·  ')}
                </span>
              </div>
            </div>
          )}

          {breakingArticles.length===0 && (
            <div style={{ background:C.card, border:`1px dashed ${C.border}`, borderRadius:10,
                           padding:32, textAlign:'center', marginBottom:16 }}>
              <Radio size={32} color={C.muted} style={{ margin:'0 auto 10px', display:'block' }}/>
              <div style={{ color:C.muted, fontSize:13 }}>
                No articles in the ticker yet. Mark published articles as "Breaking" to display them here.
              </div>
            </div>
          )}

          {/* All published articles — toggle breaking */}
          <div style={{ marginTop:8 }}>
            <div style={{ color:C.muted, fontSize:11, marginBottom:10, textTransform:'uppercase', letterSpacing:1 }}>
              All Published Articles — Toggle Breaking to add/remove from ticker
            </div>
            {articles.filter(a=>a.status==='published').map(a=>(
              <div key={a.id} style={{ background:C.card,
                                        border:`1px solid ${a.isBreaking?C.breaking+'66':C.border}`,
                                        borderRadius:8, padding:'12px 14px', marginBottom:8,
                                        display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ color:C.text, fontWeight:700, fontSize:13 }}>{a.title}</div>
                  <div style={{ color:C.muted, fontSize:11, marginTop:2 }}>
                    {a.category} · {fmtDate(a.publishedAt)}
                  </div>
                </div>
                <button onClick={()=>toggleBreaking(a)}
                  style={{ background:a.isBreaking?C.breaking:'transparent',
                             border:`1px solid ${a.isBreaking?C.breaking:C.border}`,
                             color:a.isBreaking?'#fff':C.muted,
                             borderRadius:6, padding:'5px 12px', fontSize:11, fontWeight:700,
                             cursor:'pointer', display:'flex', alignItems:'center', gap:5, flexShrink:0 }}>
                  <Zap size={11}/>
                  {a.isBreaking?'In Ticker':'Add to Ticker'}
                </button>
              </div>
            ))}
            {articles.filter(a=>a.status==='published').length===0 && (
              <div style={{ color:C.muted, fontSize:13, padding:'20px 0' }}>
                No published articles yet. Publish articles first, then add them to the ticker.
              </div>
            )}
          </div>
        </div>
      )}

      {/* NEWS PORTAL TAB */}
      {tab==='portal' && (
        <div>
          {/* Filters */}
          <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:16 }}>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="Search articles..." style={{ ...iS, width:200 }}/>
            <select value={filterCat} onChange={e=>setFilterCat(e.target.value)} style={{ ...iS, width:'auto' }}>
              <option value="All">All Categories</option>
              {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{ ...iS, width:'auto' }}>
              <option value="All">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {loading && <div style={{ color:C.muted, textAlign:'center', padding:40 }}>Loading articles...</div>}

          {!loading && portalArticles.length===0 && (
            <div style={{ background:C.card, border:`1px dashed ${C.border}`, borderRadius:12,
                           padding:48, textAlign:'center' }}>
              <Newspaper size={40} color={C.muted} style={{ margin:'0 auto 12px', display:'block' }}/>
              <div style={{ color:C.muted, fontSize:14 }}>No articles found. Click "New Article" to write the first one.</div>
            </div>
          )}

          {!loading && portalArticles.map(a=>(
            <div key={a.id} style={{ background:C.card,
                                      border:`1px solid ${a.isBreaking?C.breaking+'55':C.border}`,
                                      borderRadius:10, marginBottom:10, overflow:'hidden' }}>
              <div style={{ padding:'12px 16px', display:'flex', alignItems:'flex-start', gap:12 }}>

                {/* Featured image thumbnail */}
                {a.featuredImageUrl && (
                  <div style={{ width:52, height:52, borderRadius:6, overflow:'hidden', flexShrink:0, border:`1px solid ${C.border}` }}>
                    <img src={a.featuredImageUrl} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                  </div>
                )}

                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:4 }}>
                    <span style={{ color:C.text, fontWeight:700, fontSize:14 }}>{a.title}</span>
                    <StatusBadge status={a.status}/>
                    {a.isBreaking && (
                      <span style={{ background:C.breaking+'22', color:C.breaking, fontSize:10, fontWeight:800,
                                      padding:'2px 8px', borderRadius:20, border:`1px solid ${C.breaking}44`,
                                      display:'flex', alignItems:'center', gap:3 }}>
                        <Radio size={9}/>📡 NEWS BAR
                      </span>
                    )}
                    {a.isFeatured && (
                      <span style={{ background:'#7c3aed22', color:'#a78bfa', fontSize:10, fontWeight:700,
                                      padding:'2px 8px', borderRadius:20 }}>
                        <Star size={9} style={{ marginRight:3, verticalAlign:'middle' }}/>Featured
                      </span>
                    )}
                  </div>
                  <div style={{ color:C.muted, fontSize:11 }}>
                    {a.category} · {a.subtitle||'No author'} · {fmtDate(a.publishedAt||a.createdAt)}
                    {a.viewCount>0 && <span style={{ marginLeft:8 }}>👁 {a.viewCount}</span>}
                  </div>
                  {a.excerpt && (
                    <div style={{ color:C.muted, fontSize:12, marginTop:4,
                                   overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:500 }}>
                      {a.excerpt}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display:'flex', gap:6, flexShrink:0, alignItems:'center' }}>
                  <button onClick={()=>toggleBreaking(a)} title={a.isBreaking?'Remove from News Bar':'Add to News Bar'}
                    style={{ background:a.isBreaking?C.breaking:'transparent',
                               border:`1px solid ${a.isBreaking?C.breaking:C.border}`,
                               color:a.isBreaking?'#fff':C.muted,
                               borderRadius:6, padding:'5px 8px', cursor:'pointer',
                               display:'flex', alignItems:'center', gap:4, fontSize:11, fontWeight:700 }}>
                    <Zap size={12}/>
                    {a.isBreaking?'Ticker':'Add Ticker'}
                  </button>
                  <button onClick={()=>togglePublish(a)} title={a.status==='published'?'Unpublish':'Publish'}
                    style={{ background:'transparent', border:`1px solid ${C.border}`,
                               color:C.muted, borderRadius:6, padding:'5px 8px', cursor:'pointer' }}>
                    {a.status==='published' ? <EyeOff size={13}/> : <Eye size={13}/>}
                  </button>
                  <Btn variant='ghost' onClick={()=>openEdit(a)} style={{ padding:'5px 10px' }}>
                    <Edit2 size={13}/>
                  </Btn>
                  <Btn variant='danger' onClick={()=>setDeleteConfirm(a)} style={{ padding:'5px 10px' }}>
                    <Trash2 size={13}/>
                  </Btn>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ADD/EDIT ARTICLE MODAL */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:1000,
                       display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12,
                         width:'100%', maxWidth:680, maxHeight:'92vh', overflowY:'auto' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                           padding:'16px 20px', borderBottom:`1px solid ${C.border}`, position:'sticky', top:0,
                           background:C.card, zIndex:10 }}>
              <h3 style={{ fontFamily:'Cinzel, serif', color:C.gold, margin:0, fontSize:16 }}>
                {editArticle?'Edit Article':'New Article'}
              </h3>
              <button onClick={()=>setShowForm(false)} style={{ background:'none', border:'none', color:C.muted, cursor:'pointer' }}>
                <X size={18}/>
              </button>
            </div>
            <div style={{ padding:'20px' }}>

              {/* Breaking banner */}
              {form.isBreaking && (
                <div style={{ background:`${C.breaking}22`, border:`1px solid ${C.breaking}44`,
                               borderRadius:8, padding:'10px 14px', marginBottom:16,
                               display:'flex', alignItems:'center', gap:8 }}>
                  <Radio size={14} color={C.breaking}/>
                  <span style={{ color:C.breaking, fontSize:12, fontWeight:700 }}>
                    📡 This article will appear in the scrolling News Bar ticker on all pages (when Published)
                  </span>
                </div>
              )}

              <div style={{ display:'grid', gap:12 }}>
                <div>
                  <Label required>Headline / Title</Label>
                  <input value={form.title} onChange={e=>upd('title',e.target.value)}
                    placeholder="e.g. Grand Finale Set for December 30 at Jattu Palace" style={iS}/>
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <div>
                    <Label required>Category</Label>
                    <select value={form.category} onChange={e=>upd('category',e.target.value)} style={iS}>
                      {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Author / Byline</Label>
                    <input value={form.subtitle} onChange={e=>upd('subtitle',e.target.value)}
                      placeholder="e.g. Festival Communications Team" style={iS}/>
                  </div>
                </div>

                <div>
                  <Label>Summary / Excerpt</Label>
                  <textarea value={form.excerpt} onChange={e=>upd('excerpt',e.target.value)}
                    placeholder="Short summary shown in article listings (2-3 sentences)..."
                    rows={2} style={{ ...iS, resize:'vertical' }}/>
                </div>

                <div>
                  <Label>Full Article Content</Label>
                  <textarea value={form.content} onChange={e=>upd('content',e.target.value)}
                    placeholder="Write the full article content here..."
                    rows={8} style={{ ...iS, resize:'vertical' }}/>
                </div>

                <div>
                  <Label>Featured Image URL</Label>
                  <input value={form.featuredImageUrl} onChange={e=>upd('featuredImageUrl',e.target.value)}
                    placeholder="https://..." style={iS}/>
                  {form.featuredImageUrl && (
                    <img src={form.featuredImageUrl} alt="" onError={e=>(e.currentTarget.style.display='none')}
                      style={{ marginTop:6, height:80, borderRadius:6, objectFit:'cover', border:`1px solid ${C.border}` }}/>
                  )}
                </div>

                {/* Status */}
                <div>
                  <Label>Publication Status</Label>
                  <div style={{ display:'flex', gap:8 }}>
                    {(['draft','published','archived'] as const).map(s=>(
                      <button key={s} onClick={()=>upd('status',s)}
                        style={{ padding:'6px 14px', borderRadius:6, fontSize:12, fontWeight:700,
                                  cursor:'pointer', textTransform:'capitalize',
                                  background:form.status===s?(s==='published'?C.green:s==='archived'?C.muted:C.amber):'transparent',
                                  color:form.status===s?'#000':C.muted,
                                  border:`1px solid ${form.status===s?(s==='published'?C.green:s==='archived'?C.muted:C.amber):C.border}` }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Options */}
                <div style={{ background:'#0A120D', borderRadius:8, padding:'12px 14px' }}>
                  <div style={{ color:C.muted, fontSize:11, fontWeight:700, textTransform:'uppercase',
                                  letterSpacing:1, marginBottom:10 }}>Article Options</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>

                    {/* Breaking News */}
                    <label style={{ display:'flex', alignItems:'flex-start', gap:10, cursor:'pointer' }}>
                      <input type="checkbox" checked={form.isBreaking} onChange={e=>upd('isBreaking',e.target.checked)}
                        style={{ accentColor:C.breaking, width:15, height:15, marginTop:1 }}/>
                      <div>
                        <div style={{ color:C.breaking, fontSize:13, fontWeight:700, display:'flex', alignItems:'center', gap:5 }}>
                          <Radio size={13}/>📡 Breaking News — Add to News Bar Ticker
                        </div>
                        <div style={{ color:C.muted, fontSize:11, marginTop:2 }}>
                          When checked AND published, this headline scrolls across all pages in the News Bar
                        </div>
                      </div>
                    </label>

                    {/* Featured */}
                    <label style={{ display:'flex', alignItems:'flex-start', gap:10, cursor:'pointer' }}>
                      <input type="checkbox" checked={form.isFeatured} onChange={e=>upd('isFeatured',e.target.checked)}
                        style={{ accentColor:C.gold, width:15, height:15, marginTop:1 }}/>
                      <div>
                        <div style={{ color:C.gold, fontSize:13, fontWeight:700, display:'flex', alignItems:'center', gap:5 }}>
                          <Star size={13}/>Featured Article
                        </div>
                        <div style={{ color:C.muted, fontSize:11, marginTop:2 }}>
                          Highlighted in the News Portal and on the homepage
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              <div style={{ display:'flex', gap:10, marginTop:20 }}>
                <Btn onClick={saveArticle} disabled={saving}>
                  {saving?'Saving...': form.status==='published'?'Publish Article':'Save Draft'}
                </Btn>
                <Btn variant='ghost' onClick={()=>setShowForm(false)}>Cancel</Btn>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM */}
      {deleteConfirm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:1000,
                       display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:C.card, border:`1px solid ${C.red}44`, borderRadius:12, padding:28, maxWidth:380, width:'100%' }}>
            <h3 style={{ color:C.red, fontFamily:'Cinzel, serif', marginBottom:8 }}>Delete Article?</h3>
            <p style={{ color:C.muted, fontSize:13, marginBottom:20 }}>
              "{deleteConfirm.title}" will be permanently deleted and removed from the News Bar if active.
            </p>
            <div style={{ display:'flex', gap:10 }}>
              <Btn variant='danger' onClick={()=>deleteArticle(deleteConfirm)}>Delete</Btn>
              <Btn variant='ghost' onClick={()=>setDeleteConfirm(null)}>Cancel</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ErueNewsPortal;
